import type { ReactNode } from 'react';

import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import Text from '../../../components/Text';

type Props = {
  title: string;
  count: number;
  subtotal: string;
  children: ReactNode;
  /** The count chip next to the title.  "Other work" hides it. */
  showCount?: boolean;
};

/**
 * A report list - the section name with its count and time subtotal, above
 * the card of rows it refers to.
 */
export default function ReportSection({
  title,
  count,
  subtotal,
  children,
  showCount = true,
}: Props) {
  return (
    <html.div style={styles.section}>
      <html.div style={styles.head}>
        <html.div style={styles.headLeft}>
          <Text typeface="body.medium" color="neutral100">
            {title}
          </Text>
          {showCount && (
            <html.span style={styles.count}>
              <Text typeface="body.small.medium" color="neutral100">
                {count}
              </Text>
            </html.span>
          )}
        </html.div>
        <Text typeface="body.small.medium" color="neutral60">
          {subtotal}
        </Text>
      </html.div>
      <html.div style={styles.list}>{children}</html.div>
    </html.div>
  );
}

const styles = css.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  head: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexGrow: 1,
  },
  count: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: 6,
    borderRadius: 8,
    backgroundColor: color.neutral20,
    height: 20,
    minWidth: 20,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    overflow: 'hidden',
  },
});
